import { useCallback, useMemo, useState } from 'react';
import { PageBody, PageHeader } from '@/components/layout/page-header';
import { DataTable, TabelaPainel, type Coluna } from '@/components/patterns/data-table';
import { FilterBar, FilterBusca, FilterLimpar, FilterPeriodo, FilterSelect } from '@/components/patterns/filter-bar';
import { StatusHistoricoBadge } from '@/components/patterns/status-badge';
import { Badge } from '@/components/ui/badge';
import { dataSource } from '@/data';
import type { EventoHistorico, Instalacao } from '@/data/types';
import { useResource } from '@/hooks/use-resource';
import { formatarDataHora, plural, texto } from '@/lib/format';
import { noPeriodo, PERIODO_PRESETS } from '@/lib/periodo';

/**
 * Histórico de atualizações.
 *
 * Ordenado do evento mais recente para o mais antigo por padrão — quem abre
 * esta tela normalmente está investigando o que acabou de acontecer. Os filtros
 * (instalação e status) são os mesmos declarados no schema da entidade no app
 * original; a lista de instalações é montada a partir dos dados carregados, em
 * vez de vir fixa num enum de schema.
 */
/** `EventoHistorico.data` já vem como datetime completo ("YYYY-MM-DD HH:mm",
 * ao contrário de `Agendamento`, que separa `data`/`hora`) — não precisa
 * combinar nada, só validar que dá um instante real. */
function instanteEvento(item: EventoHistorico): number | null {
  if (!item.data) return null;
  const instante = new Date(item.data).getTime();
  return Number.isNaN(instante) ? null : instante;
}

export function HistoricoPage() {
  const [busca, setBusca] = useState('');
  const [instalacao, setInstalacao] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [periodo, setPeriodo] = useState<string | null>(null);
  const [periodoDe, setPeriodoDe] = useState('');
  const [periodoAte, setPeriodoAte] = useState('');

  // Sem intervalo customizado, o backend já limita a janela sozinho (~30
  // dias, nunca mais que ~31) — só precisa recarregar do servidor quando um
  // "De"/"Até" específico é aplicado (pode ser uma janela fora do default) ou
  // removido (volta pro default). Presets (1h/24h/7d) continuam um refiltro
  // só no cliente, sem ida ao servidor.
  const carregar = useCallback(
    async (signal: AbortSignal) => {
      const [historico, instalacoes] = await Promise.all([
        dataSource.listHistorico(signal, { de: periodoDe || undefined, ate: periodoAte || undefined }),
        dataSource.listInstalacoes(signal),
      ]);
      return { historico, instalacoes };
    },
    [periodoDe, periodoAte],
  );

  const recurso = useResource<{ historico: EventoHistorico[]; instalacoes: Instalacao[] }>(carregar, [
    periodoDe,
    periodoAte,
  ]);

  const eventos = useMemo(() => recurso.dados?.historico ?? [], [recurso.dados]);

  const opcoesInstalacao = useMemo(() => {
    const nomes = new Set(eventos.map((item) => item.instalacao).filter(Boolean));
    return [...nomes].sort((a, b) => a.localeCompare(b, 'pt-BR')).map((nome) => ({ valor: nome, rotulo: nome }));
  }, [eventos]);

  const filtrados = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return eventos
      .filter((item) => {
        if (instalacao && item.instalacao !== instalacao) return false;
        if (status && item.status !== status) return false;
        if (!noPeriodo(instanteEvento(item), periodo, periodoDe, periodoAte)) return false;
        if (!termo) return true;
        return `${item.instalacao} ${item.versao} ${item.origem} ${item.batchId ?? ''}`.toLowerCase().includes(termo);
      })
      .sort((a, b) => (b.data ?? '').localeCompare(a.data ?? ''));
  }, [eventos, busca, instalacao, status, periodo, periodoDe, periodoAte]);

  const filtrosAtivos = [busca.trim() !== '', instalacao !== null, status !== null, periodo !== null].filter(
    Boolean,
  ).length;

  const colunas: Array<Coluna<EventoHistorico>> = [
    {
      id: 'data',
      cabecalho: 'Quando',
      largura: '190px',
      ordenarPor: (item) => item.data ?? '',
      celula: (item) => <span className="tabular-nums">{formatarDataHora(item.data)}</span>,
    },
    {
      id: 'instalacao',
      cabecalho: 'Instalação',
      ordenarPor: (item) => item.instalacao,
      celula: (item) => <span className="font-bold">{texto(item.instalacao)}</span>,
    },
    {
      id: 'versao',
      cabecalho: 'Versão',
      largura: '130px',
      ordenarPor: (item) => item.versao,
      celula: (item) => <span className="font-narrow text-[15px] font-bold tabular-nums">{texto(item.versao)}</span>,
    },
    { id: 'status', cabecalho: 'Resultado', largura: '170px', celula: (item) => <StatusHistoricoBadge valor={item.status} /> },
    {
      id: 'origem',
      cabecalho: 'Origem',
      largura: '210px',
      secundaria: true,
      celula: (item) => (
        <div className="flex flex-col items-start gap-1">
          <span>{texto(item.origem)}</span>
          {item.batchId ? (
            <Badge tone="primary">
              Lote <span className="font-mono text-[10px]">{item.batchId}</span>
            </Badge>
          ) : null}
        </div>
      ),
    },
    {
      id: 'id',
      cabecalho: 'Registro',
      largura: '140px',
      secundaria: true,
      celula: (item) => <span className="font-mono text-[11px] text-on-surface-muted">{texto(item.id)}</span>,
    },
  ];

  return (
    <PageBody>
      <PageHeader
        eyebrow="Atualizações"
        titulo="Histórico"
        descricao="Atualizações concluídas, falhas e cancelamentos das instalações do escopo, do mais recente para o mais antigo."
      />

      <TabelaPainel
        titulo="Eventos"
        contagem={recurso.estado === 'pronto' ? plural(filtrados.length, 'evento', 'eventos') : undefined}
        filtros={
          <FilterBar>
        <FilterBusca
          id="filtro-busca-historico"
          label="Buscar no histórico"
          valor={busca}
          aoMudar={setBusca}
          placeholder="Instalação, versão, origem ou lote"
        />
        <FilterSelect
          id="filtro-instalacao-historico"
          label="Instalação"
          valor={instalacao}
          aoMudar={setInstalacao}
          rotuloTodos="Todas as instalações"
          opcoes={opcoesInstalacao}
        />
        <FilterSelect
          id="filtro-status-historico"
          label="Resultado"
          valor={status}
          aoMudar={setStatus}
          rotuloTodos="Qualquer resultado"
          opcoes={[
            { valor: 'Concluído', rotulo: 'Concluído' },
            { valor: 'Falha', rotulo: 'Falhou' },
            { valor: 'Falha Parcial', rotulo: 'Falha parcial' },
            { valor: 'Programado', rotulo: 'Programado' },
            { valor: 'Cancelado', rotulo: 'Cancelado' },
          ]}
        />
        <FilterPeriodo
          id="filtro-periodo-historico"
          periodo={periodo}
          de={periodoDe}
          ate={periodoAte}
          aoMudar={({ periodo: proximo, de, ate }) => {
            setPeriodo(proximo);
            setPeriodoDe(de);
            setPeriodoAte(ate);
          }}
          presets={PERIODO_PRESETS}
          limiteDias={31}
        />
        <FilterLimpar
          ativo={filtrosAtivos > 0}
          quantidade={filtrosAtivos}
          aoLimpar={() => {
            setBusca('');
            setInstalacao(null);
            setStatus(null);
            setPeriodo(null);
            setPeriodoDe('');
            setPeriodoAte('');
          }}
        />
          </FilterBar>
        }
      >
          <DataTable
            rotulo="Histórico de atualizações"
            itens={filtrados}
            colunas={colunas}
            chave={(item) => item.id}
            estado={recurso.estado}
            erro={recurso.erro}
            aoTentarDeNovo={recurso.recarregar}
            vazio={
              filtrosAtivos > 0
                ? {
                    titulo: 'Nenhum evento com esses filtros',
                    descricao: 'Ajuste os filtros para ver outros eventos.',
                  }
                : {
                    titulo: 'Histórico vazio',
                    descricao: 'Ainda não há atualizações registradas para as instalações do seu escopo.',
                  }
            }
          />
      </TabelaPainel>
    </PageBody>
  );
}
